/**
 * HSE raporlarının sabitleri (ADR 0049): dört tür, durumlar, listeler. Listeler BGM
 * `gemi-hse-genel.jpeg` "Alt kırılımlar" ve ortağın şartnamesinden
 * (`docs/gereksinimler/ortak-2026-10-13/raporlar-nc-deficiency-nearmiss-accident.pdf`).
 * Değerler koddur; arayüz `hse.*` anahtarlarıyla çevirir.
 */

/** [ISM 9.1] uygunsuzluk, kaza ve tehlikeli durum (near miss) raporları; [ISM 10.2] eksiklik. */
export const HSE_REPORT_TYPES = ['non_conformity', 'deficiency', 'near_miss', 'accident'] as const;
export type HseReportType = (typeof HSE_REPORT_TYPES)[number];

/**
 * Numara öneki (ortak 2026-10-20b H1: NC / DF / NM / ACC; ADR 0097 §2). Eksikliğin eski öneki
 * `DEF-` arızanın numarasıyla (`DEF-YYYY-NNNN`) karışıyordu; yeni eksiklik `DF-` alır, eski
 * numaralar değişmez (`HSE_LEGACY_PREFIXES`).
 */
export const HSE_PREFIXES: Readonly<Record<HseReportType, string>> = {
  non_conformity: 'NC',
  deficiency: 'DF',
  near_miss: 'NM',
  accident: 'ACC',
};

/** Eski önekler (ADR 0097 §2): bu önekle açılmış numara geçerli kalır, yeniden yazılmaz. */
export const HSE_LEGACY_PREFIXES: Readonly<Partial<Record<HseReportType, readonly string[]>>> = {
  deficiency: ['DEF'],
};

/**
 * HSEQ raporunun dört ana aşaması (ortak 2026-10-20b H1, `hseq-1-tipler.jpeg`; ADR 0097 §3):
 * kullanıcı ana ekranda yalnız bunları görür; ayrıntılı durum rozette ve geçmişte kalır.
 */
export const HSEQ_STAGES = ['reported', 'under_action', 'verification', 'closed'] as const;
export type HseqStage = (typeof HSEQ_STAGES)[number];

/** Risk değerlendirmesinin olasılık ve şiddet ölçeği (1–5; risk modülüyle aynı, ADR 0085). */
export const HSEQ_RISK_SCALE = [1, 2, 3, 4, 5] as const;

/** Eksikliğin dış referansının kaynağı (H6: PSC / Flag / Class). */
export const HSE_EXTERNAL_AUTHORITIES = ['psc', 'flag', 'class', 'other'] as const;

/** Kapanış onayının kayıt türü (onay matrisi; ADR 0049 §4). */
export const HSE_RECORD_TYPES = {
  non_conformity: 'hse_non_conformity',
  deficiency: 'hse_deficiency',
  near_miss: 'hse_near_miss',
  accident: 'hse_accident',
} as const satisfies Record<HseReportType, string>;
export type HseRecordType = (typeof HSE_RECORD_TYPES)[HseReportType];

export function hseRecordTypeOf(type: HseReportType): HseRecordType {
  return HSE_RECORD_TYPES[type];
}

export function hseTypeOfRecordType(recordType: string): HseReportType | null {
  const found = HSE_REPORT_TYPES.find((type) => HSE_RECORD_TYPES[type] === recordType);
  return found ?? null;
}

/** Görünen durum (ortak §5); türetilir (`deriveHseStatus`). */
export const HSE_STATUSES = [
  'draft',
  'submitted',
  'under_review',
  'action_assigned',
  'in_progress',
  'pending_verification',
  'closed',
  'reopened',
] as const;
export type HseStatus = (typeof HSE_STATUSES)[number];

/** Açık sayılan durumlar (liste kartı "Open"). */
export const HSE_OPEN_STATUSES: readonly HseStatus[] = [
  'submitted',
  'under_review',
  'action_assigned',
  'in_progress',
  'pending_verification',
  'reopened',
];

/** Öncelik ve risk seviyesi (BGM: düşük, orta, yüksek, kritik). */
export const HSE_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type HsePriority = (typeof HSE_PRIORITIES)[number];

export const HSE_DEPARTMENTS = [
  'deck',
  'engine',
  'electrical',
  'bridge',
  'galley',
  'accommodation',
  'safety',
  'office',
  'other',
] as const;

/** Türün kategorileri (BGM `gemi-hse-genel.jpeg` alt kırılımları ve liste "Type" sütunu). */
export const HSE_CATEGORIES: Readonly<Record<HseReportType, readonly string[]>> = {
  non_conformity: [
    'procedure',
    'instruction',
    'training',
    'administrative',
    'equipment',
    'documentation',
    'psc',
    'other',
  ],
  deficiency: ['equipment', 'maintenance', 'safety_equipment', 'documentation', 'other'],
  near_miss: ['collision_risk', 'slip_trip_fall', 'equipment_failure', 'fire_risk', 'other'],
  accident: ['personnel_injury', 'property_damage', 'environmental', 'vessel_damage', 'other'],
};

/** Eksikliğin alt kategorisi (BGM "Sub Category": Machinery …). */
export const HSE_SUB_CATEGORIES = [
  'machinery',
  'electrical',
  'navigation',
  'safety',
  'hull_deck',
  'cargo',
  'accommodation',
  'other',
] as const;

/** Uygunsuzluğun kaynağı (BGM "Source": PSC Inspection …). */
export const NC_SOURCES = [
  'internal_audit',
  'external_audit',
  'psc_inspection',
  'flag_inspection',
  'class_survey',
  'vetting',
  'self_detected',
  'other',
] as const;

/** [ISM 1.1.9, 1.1.10] uygunsuzluk, büyük uygunsuzluk ve gözlem. */
export const NC_CLASSES = ['major', 'minor', 'observation'] as const;

export const PSC_MOUS = [
  'paris',
  'tokyo',
  'uscg',
  'black_sea',
  'mediterranean',
  'indian_ocean',
  'other',
] as const;
export const PSC_DOCUMENTS = ['psc_report', 'form_a', 'form_b', 'other'] as const;
export const PSC_OVERALL_FINDINGS = [
  'no_deficiencies',
  'remarks_issued',
  'deficiencies',
  'detention',
] as const;

/** Ekipman kullanılabilir mi (ortak §4B). */
export const EQUIPMENT_USABILITY = ['yes', 'restricted', 'no'] as const;

/** Faaliyet türü: düzeltme, düzeltici ve önleyici [ISM 9.2 "tekrarını önleme"]. */
export const HSE_ACTION_KINDS = ['correction', 'corrective', 'preventive'] as const;
export type HseActionKind = (typeof HSE_ACTION_KINDS)[number];

export const HSE_ACTION_STATUSES = ['planned', 'in_progress', 'completed', 'cancelled'] as const;
export type HseActionStatus = (typeof HSE_ACTION_STATUSES)[number];

/** PSC remark'ının ya da bulgunun durumu (bağlı faaliyetlerden türer). */
export const HSE_FINDING_STATUSES = ['open', 'planned', 'in_progress', 'closed'] as const;
export type HseFindingStatus = (typeof HSE_FINDING_STATUSES)[number];

/**
 * Kaza bildirimi (ADR 0049 §10): karar ve alıcılar. Süre sistemde hesaplanmaz; kişi her bildirimin
 * son gününü girer (ADR 0097 §5).
 */
export const NOTIFICATION_DECISIONS = ['required', 'not_required', 'undetermined'] as const;
export const NOTIFICATION_RECIPIENTS = [
  'flag_state',
  'class',
  'port_state',
  'coastal_state',
  'p_and_i',
  'company',
  'other',
] as const;

/** Ofis notunun türü (ADR 0049 §7). */
export const HSE_OFFICE_NOTE_KINDS = [
  'comment',
  'action_request',
  'reopen_request',
  'report_request',
] as const;
export type HseOfficeNoteKind = (typeof HSE_OFFICE_NOTE_KINDS)[number];

/** Kök neden en az bu kadar karakter (yalnız "dikkatsizlik" yazıp kapatmayı önler; ortak §4A). */
export const ROOT_CAUSE_MIN_LENGTH = 20;

/** `details` JSON'unun şema sürümü. */
export const HSE_DETAILS_SCHEMA = 1;
